<template>
  <RouterLink v-if="getUserData" class="post" :to="linkOnPost(post.id)">
    <div class="post__avatar-container">
      <BaseAvatar
        :storage-id="getUserData.avatarFile?.storageId"
        :verified="getUserData.verified"
        :href="userRoute"
      />
      <div class="post__badge post__badge--gradient mx-auto">
        {{ abbreviateNumber(getUserData.rating, "short") }}
      </div>
    </div>
    <div class="post__body">
      <div class="post__header">
        <div class="post__user-info">
          <router-link :to="userRoute" class="post__username hover:underline">{{
            fullName
          }}</router-link>
          <span
            v-if="getUserData.username"
            class="post__username post__username--secondary"
            >@{{ getUserData.username }}</span
          >
        </div>
        <span class="post__date mb-auto">{{ formattedTime }}</span>
      </div>
      <div class="post__content">
        <p v-if="post.title" class="post__text font-bold">
          {{ coretedText(post.title) }}
        </p>
        <p v-if="post.text" class="post__text">
          {{ coretedText(post.text) }}
        </p>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import { abbreviateNumber } from "@/utils/utils.js";
import { computed } from "vue";
import { getRelativeTime } from "@/utils/relativeTime.js";
import { useStore } from "vuex";

const props = defineProps({
  post: {
    type: Object,
    default: null,
  },
});

const store = useStore();
const getUserData = computed(() => props.post.authorUser || null);

const fullName = computed(() => {
  let firstName = getUserData.value.firstName;
  let lastName = getUserData.value.lastName;
  let maxWidth = 9;

  firstName =
    firstName?.length <= maxWidth
      ? firstName
      : firstName?.slice(0, maxWidth) + "..";
  lastName =
    lastName?.length <= maxWidth
      ? lastName
      : lastName?.slice(0, maxWidth) + "..";
  if (!getUserData.value.firstName) {
    return getUserData.value.username;
  } else {
    return firstName + " " + lastName;
  }
});
const userRoute = computed(() => {
  return !getUserData.value.username
    ? `/profile/${getUserData.value?.id}`
    : `/user/${getUserData.value?.username}`;
});
const linkOnPost = (id) => {
  return {
    name: props.post.type === "reply" ? "reply" : "post",
    params: { id },
  };
};
const coretedText = (text) => {
  if (!text) {
    return "No content available";
  }
  const cleanText = text.replace(/<.*?>/g, " ").trim();
  return cleanText.slice(0, 100);
};

const now = computed(() => store.getters.getCurrentTime);
const formattedTime = computed(() => {
  return getRelativeTime(props.post.publishedAt, now, true);
});
</script>

<style scoped lang="scss">
.post {
  padding: 12px 0;
  display: flex;
  align-items: flex-start;
  transition: 0.2s ease-in;
  border-bottom: 1px solid var(--post-border);
  border-radius: 0;
  &__avatar-container {
    width: 48px;
    flex-shrink: 0;
  }
  &__user-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__username {
    align-self: flex-start;
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 108px;
    font-weight: 400 !important;

    &--secondary {
      color: var(--text-02);
      font-size: 0.75rem;
    }
  }
  &__body {
    flex-grow: 1;
    overflow-wrap: break-word;
    overflow: hidden;
  }

  &__badge {
    width: 48px;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1px;
    margin-bottom: 4px;
  }

  &__content {
    margin-top: 4px;

    .post__text {
      color: var(--foreground-primary);
      font-size: 0.8rem;
      text-align: start;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0;
      padding: 0;
    }
  }
}
</style>